<template>
  <div class="tw-group tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 hover:tw-shadow-lg tw-transition-all tw-duration-300 hover:tw-border-gray-300 tw-overflow-hidden tw-relative">
    <!-- Status Badge - Modern -->
    <div class="tw-absolute tw-top-2 tw-right-2 tw-z-10">
      <span :class="[
        'tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded-md',
        getStatusBadgeClass(session)
      ]">
        <icon :icon="getStatusIcon(session)" class="tw-text-xs" />
        {{ getStatusText(session) }}
      </span>
    </div>

    <!-- Hero Media - Modern -->
    <div class="tw-relative tw-w-full">
      <div class="tw-relative tw-w-full" style="padding-top: 50%;">
        <img
          v-if="session.image"
          :src="session.image"
          :alt="session.name"
          class="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-300 group-hover:tw-scale-105"
          loading="lazy"
          @load="onImageLoad"
        />
        <div v-else class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-100">
          <icon :icon="getSessionTypeIcon(session.type)" class="tw-text-3xl tw-text-gray-400" />
        </div>
        <div v-if="!imageLoaded && session.image" class="tw-absolute tw-inset-0 tw-animate-pulse tw-bg-gray-200"></div>
      </div>
    </div>

    <!-- Content - Modern -->
    <div class="tw-p-3">

      <div class="tw-flex tw-items-center tw-justify-between">

        <!-- Title -->
        <h3 class="tw-text-sm tw-font-bold tw-text-gray-900 tw-mb-2 tw-line-clamp-2 group-hover:tw-text-gray-700 tw-transition-colors tw-duration-200">
          {{ session.name }}
        </h3>
  
        <!-- Topic - Modern -->
        <div v-if="session.topic" class="tw-mb-2">
          <span class="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-50 tw-text-gray-600 tw-border tw-border-gray-200">
            {{ session.topic }}
          </span>
        </div>
      </div>

      <!-- Description -->
      <p v-if="session.description" class="tw-text-xs tw-text-gray-500 tw-mb-3 tw-line-clamp-2">
        {{ session.description }}
      </p>

      <!-- Meta - Modern -->
      <div class="tw-space-y-2 tw-mb-3">
        <div class="tw-flex tw-items-center tw-gap-2 tw-text-xs">
          <span :class="['tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded', getLanguageBadgeClass(session.language)]">
            {{ session.language }}
          </span>
          <span v-if="session.type" class="tw-text-gray-500">{{ session.type }}</span>
        </div>
        <div class="tw-flex tw-items-center tw-gap-3 tw-text-xs tw-text-gray-500">
          <span class="tw-flex tw-items-center tw-gap-1">
            <icon icon="solar:calendar-bold" class="tw-text-gray-400" />
            {{ formatSessionDate(session.start_date, session.start_time) }}
          </span>
          <span class="tw-flex tw-items-center tw-gap-1">
            <icon icon="solar:clock-circle-bold" class="tw-text-gray-400" />
            {{ formatDuration(session.start_time, session.end_time) }}
          </span>
        </div>
      </div>

      <!-- Speakers - Modern -->
      <div class="tw-mb-3">
        <div v-if="session.speakers && session.speakers.length > 0" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-1">
          <div
            v-for="(speaker) in session.speakers.slice(0, 4)"
            :key="speaker.id"
            class="tw-flex tw-items-center tw-gap-1"
          >
            <img 
              :src="speaker.profile_image" 
              :alt="speaker.name" 
              class="tw-w-6 tw-h-6 tw-rounded-full tw-object-cover tw-border tw-border-white" 
            />
            <span class="tw-text-xs tw-text-gray-600 tw-font-medium">{{ speaker.name }}</span>
          </div>
          <!-- More speakers indicator -->
          <div v-if="session.speakers.length > 4" class="tw-text-xs tw-text-gray-400 tw-col-span-1 sm:tw-col-span-2">
            +{{ session.speakers.length - 4 }} more speakers
          </div>
        </div>
        <div v-else class="tw-text-xs tw-text-gray-400">
          No speakers
        </div>
      </div>

      <!-- Actions - Modern -->
      <div class="tw-flex tw-items-center tw-justify-between">
        <span class="tw-text-xs tw-text-gray-400">{{ session.attendees_count || 0 }} attending</span>
        
        <div class="tw-flex tw-gap-1">
          <a-button
            v-if="showJoinButton && !isCurrentUserSpeaker"
            @click="handleJoin"
            :loading="loading"
            :disabled="loading"
            :intent="session.is_joined ? 'danger' : 'primary'"
            size="xs"
            class="tw-text-xs tw-px-2 tw-py-1"
            :left-icon="session.is_joined ? 'solar:minus-circle-bold' : 'heroicons:plus-16-solid'"
          >
            {{ session.is_joined ? 'Leave' : 'Join' }}
          </a-button>

          <a-button
            @click="handleView"
            intent="secondary"
            size="xs"
            class="tw-text-xs tw-px-2 tw-py-1"
            left-icon="solar:eye-bold"
          >
            View
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue'
import moment from 'moment'
import router from '@/router'
import { useAuthStore } from '@/stores'

const props = defineProps({
  session: {
    type: Object,
    required: true
  },
  showJoinButton: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['join'])

const authStore = useAuthStore()
const imageLoaded = ref(false)

// Computed property to check if current user is a speaker
const isCurrentUserSpeaker = computed(() => {
  if (!authStore.user?.id || !props.session?.speakers) return false
  return props.session.speakers.some(speaker => speaker.id === authStore.user.id)
})

const onImageLoad = () => {
  imageLoaded.value = true
}

// Status helpers
const getStatusText = (session) => {
  const now = moment()
  const sessionStart = moment(`${session.start_date} ${session.start_time}`, 'YYYY-MM-DD HH:mm')
  const sessionEnd = moment(`${session.start_date} ${session.end_time}`, 'YYYY-MM-DD HH:mm')
  
  if (now.isBefore(sessionStart)) {
    return 'Upcoming'
  } else if (now.isBetween(sessionStart, sessionEnd)) {
    return 'Live'
  } else {
    return 'Completed'
  }
}

const getStatusIcon = (session) => {
  const status = getStatusText(session)
  const statusIcons = {
    'Upcoming': 'solar:clock-circle-bold',
    'Live': 'solar:play-circle-bold',
    'Completed': 'solar:check-circle-bold'
  }
  return statusIcons[status] || 'solar:clock-circle-bold'
}

const getStatusBadgeClass = (session) => {
  const status = getStatusText(session)
  const statusClasses = {
    'Upcoming': 'tw-bg-blue-50 tw-text-blue-600',
    'Live': 'tw-bg-green-50 tw-text-green-600',
    'Completed': 'tw-bg-gray-50 tw-text-gray-600'
  }
  return statusClasses[status] || 'tw-bg-gray-50 tw-text-gray-600'
}

// Helpers
const formatSessionDate = (date, time) => {
  if (!date) return ''
  const dateObj = moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm')
  const today = moment()
  const tomorrow = moment().add(1, 'day')

  if (dateObj.isSame(today, 'day')) {
    return `Today ${dateObj.format('h:mm A')}`
  } else if (dateObj.isSame(tomorrow, 'day')) {
    return `Tomorrow ${dateObj.format('h:mm A')}`
  } else {
    return dateObj.format('MMM D, h:mm A')
  }
}

const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return ''
  const start = moment(startTime, 'HH:mm')
  const end = moment(endTime, 'HH:mm')
  const duration = moment.duration(end.diff(start))
  const hours = Math.floor(duration.asHours())
  const minutes = duration.minutes()
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

const getSessionTypeIcon = (type) => {
  const typeIcons = {
    'Meeting': 'solar:users-group-rounded-bold-duotone',
    'Webinar': 'solar:monitor-bold-duotone',
    'Conference': 'solar:buildings-3-bold-duotone',
    'Workshop': 'solar:widget-4-bold-duotone',
    'Presentation': 'solar:presentation-graph-bold-duotone'
  }
  return typeIcons[type] || 'solar:calendar-bold-duotone'
}

const getLanguageBadgeClass = (language) => {
  const languageClasses = {
    'English': 'tw-bg-blue-50 tw-text-blue-600',
    'Spanish': 'tw-bg-yellow-50 tw-text-yellow-600',
    'French': 'tw-bg-pink-50 tw-text-pink-600',
    'Arabic': 'tw-bg-amber-50 tw-text-amber-600'
  }
  return languageClasses[language] || 'tw-bg-gray-50 tw-text-gray-600'
}

const handleJoin = () => {
  emit('join', props.session)
}

const handleView = () => {
  router.push({ name: 'shared.agenda-session', params: { id: props.session.id } })
}
</script>

<style scoped>
.tw-line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
